import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

const Donation = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    email: "",
    amount: "",
    currency: "USD",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamically load the Squad widget script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.squadco.com/widget/squad.min.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handlePayment = () => {
    if (typeof window.squad === "undefined") {
      toast({
        title: "Error",
        description: "Payment widget failed to load. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    const squadInstance = new window.squad({
      onClose: () => console.log("Payment widget closed"),
      onLoad: () => console.log("Payment widget loaded"),
      onSuccess: () => {
        toast({
          title: "Payment Successful",
          description: "Thank you for your donation!",
        });

        // Clear the form inputs
        setFormData({
          email: "",
          amount: "",
          currency: "USD",
        });
      },
      key: "pk_64b83f80949bc3031acbfe17a5666ce834af66af",
      email: formData.email,
      amount: parseInt(formData.amount, 10) * 100,
      currency_code: formData.currency,
    });

    squadInstance.setup();
    squadInstance.open();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      handlePayment();
    } catch (error) {
      console.error("Error in donation process:", error);
      toast({
        title: "Error",
        description: "There was an error processing your donation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sage-50 to-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-serif font-bold text-sage-800 mb-8">Donate</h1>
        <div className="bg-white rounded-lg shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-sage-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-sage-700 mb-1">
                Currency
              </label>
              <select
                id="currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2"
              >
                <option value="USD">USD</option>
                <option value="NGN">NGN</option>
              </select>
            </div>
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-sage-700 mb-1">
                Amount ({formData.currency})
              </label>
              <Input
                id="amount"
                type="number"
                min={formData.currency === "USD" ? "10" : "100"} // 10 USD or 100 NGN
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? "Processing..." : "Donate Now"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Donation;
